import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { ApprovalQueue } from "@/components/admin/wi/approve/ApprovalQueue";
import { ApprovalHistory } from "@/components/admin/wi/approve/ApprovalHistory";
import { ApproveDrawer } from "@/components/admin/wi/approve/ApproveDrawer";
import { OutlookCard } from "@/components/admin/wi/approve/OutlookCard";
import { btnGhost } from "@/components/admin/wi/ui";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";

export default function WiApprovals() {
  const [queue, setQueue] = useState(null);
  const [hist, setHist] = useState(null);
  const [page, setPage] = useState(0);
  const [outlook, setOutlook] = useState(null);
  const [open, setOpen] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => {
    const fail = (e) => setLoadErr(getErrorMessage(e));
    adminApi.get("/admin/wi/approvals/queue").then(({ data }) => { setQueue(data); setLoadErr(null); }).catch(fail);
    adminApi.get("/admin/wi/approvals", { params: { page } }).then(({ data }) => setHist(data)).catch(fail);
    adminApi.get("/admin/wi/outlook").then(({ data }) => setOutlook(data)).catch(fail);
  }, [page]);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 30000, !open);
  useEffect(() => {
    const id = params.get("claim");
    if (id) { setOpen({ claim_id: id }); setParams({}, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => {
    try { await adminApi.post("/admin/wi/approvals/refresh"); toast.success("Checking Monday.com for new claims…"); setTimeout(load, 4000); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <div data-testid="wi-approvals-page">
      <AdminHeader title="Approvals" subtitle="A person approves each claim. The app then gives it the next OTMA number, updates both Monday.com boards and gets the emails ready."
        actions={<button className={btnGhost} onClick={refresh} data-testid="wi-approvals-refresh-btn"><ArrowsClockwise size={15} /> Check Monday for new claims</button>} />
      {loadErr && <div className="mx-8 mt-6 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3" data-testid="wi-approvals-load-error">Couldn't load approvals: {loadErr}</div>}
      <div className="p-8 grid xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-6">
          <ApprovalQueue data={queue} onOpen={(r) => setOpen({ ...r, claim_id: r.id })} />
          <ApprovalHistory data={hist} page={page} setPage={setPage} onOpen={(a) => setOpen({ ...a, claim_id: a.claim_id, approval: a })} />
        </div>
        <OutlookCard o={outlook} onChange={setOutlook} />
      </div>
      {open && <ApproveDrawer key={open.claim_id} target={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
